import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import type { AgendaEvent } from '../hooks/useWidgetsData';
import { useSpatialNav } from '../hooks/useSpatialNav';
import { useSettings } from '../store/settings';

/** The day (if any) whose events are open in the pop-up – from a calendar's "+N more". */
export const useDayEvents = create<{ day: Date | null; events: AgendaEvent[]; open: (day: Date, events: AgendaEvent[]) => void; close: () => void }>()(
  (set) => ({
    day: null,
    events: [],
    open: (day, events) => set({ day, events }),
    close: () => set({ day: null, events: [] }),
  }),
);

/** Every event on one day, for days with more than the calendar grid has room for. */
export function DayEventsModal() {
  const { day, events, close } = useDayEvents();
  const hour12 = useSettings((s) => s.clock.hour12);
  const dialog = useRef<HTMLDivElement>(null);
  useSpatialNav(dialog, day !== null);

  useEffect(() => {
    if (!day) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'GoBack' || e.key === 'BrowserBack') close();
    };
    window.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => dialog.current?.querySelector<HTMLElement>('button')?.focus(), 50);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [day, close]);

  const fmtTime = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', hour12 });
  const sorted = [...events].sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.start.localeCompare(b.start));

  return (
    <AnimatePresence>
      {day && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
        >
          <motion.div
            ref={dialog}
            role="dialog"
            aria-modal="true"
            aria-label="Events"
            className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-2xl bg-[var(--surface-strong)] p-6 text-[var(--fg)] shadow-2xl"
            initial={{ y: 24, scale: 0.98 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 24, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between gap-4">
              <h2 className="text-xl font-semibold">
                {new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long' }).format(day)}
              </h2>
              <button onClick={close} className="rounded-full p-2 hover:bg-[var(--border)]" aria-label="Close">
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <ul className="space-y-3">
              {sorted.map((e) => (
                <li key={`${e.uid}-${e.start}`} className="flex items-start gap-3">
                  <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full" style={{ background: e.calendar.color }} aria-hidden />
                  <span className="muted w-28 shrink-0 text-sm tabular-nums">
                    {e.allDay ? 'All day' : `${fmtTime.format(new Date(e.start))}–${fmtTime.format(new Date(e.end))}`}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block">{e.title}</span>
                    {e.location && <span className="muted block text-sm">{e.location}</span>}
                    <span className="muted block text-xs">{e.calendar.name}</span>
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
